import { useEffect, useState } from "react";
import { Copy, Download, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useConfirmTwoFactorSetup,
  useStartTwoFactorSetup,
  type TwoFactorSetup,
} from "@/lib/auth-me";
import { useToast } from "@/components/v2/Toast";

type Step = "scan" | "confirm" | "recovery";

/**
 * Three-step 2FA setup wizard:
 *   1. Scan — show QR + secret. User adds the entry to their
 *      authenticator app.
 *   2. Confirm — enter the first 6-digit code from the app. Backend
 *      flips `mfa_enabled = true` only after this passes.
 *   3. Recovery — display 10 single-use recovery codes once. User
 *      must tick "I've saved them" to close.
 */
export function TwoFactorSetupDialog({
  open,
  onClose,
  onEnabled,
}: {
  open: boolean;
  onClose: () => void;
  onEnabled?: () => void;
}) {
  const start = useStartTwoFactorSetup();
  const confirm = useConfirmTwoFactorSetup();
  const toast = useToast();

  const [step, setStep] = useState<Step>("scan");
  const [setup, setSetup] = useState<TwoFactorSetup | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [savedAcked, setSavedAcked] = useState(false);

  // Kick off setup on open.
  useEffect(() => {
    if (!open) return;
    setStep("scan");
    setCode("");
    setError(null);
    setRecoveryCodes([]);
    setSavedAcked(false);
    start.mutateAsync().then(setSetup).catch((e) => setError((e as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onConfirm = async () => {
    setError(null);
    try {
      const res = await confirm.mutateAsync(code.trim());
      setRecoveryCodes(res.recoveryCodes);
      setStep("recovery");
      onEnabled?.();
    } catch (e) {
      setError(humanError((e as Error).message));
    }
  };

  const handleClose = () => {
    if (step === "recovery" && !savedAcked) {
      // Soft block — they must tick the checkbox first.
      toast.warning(
        "Save your recovery codes",
        "Confirm you've saved them before closing.",
      );
      return;
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md">
        {step === "scan" && (
          <>
            <DialogHeader>
              <DialogTitle>Set up two-factor authentication</DialogTitle>
              <DialogDescription>
                Scan the QR with Google Authenticator, 1Password, Authy, or any
                TOTP app.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 items-center">
              {start.isPending && !setup && (
                <p className="text-sm text-muted-foreground text-center py-8">
                  Generating secret…
                </p>
              )}
              {setup && (
                <>
                  <div className="flex justify-center">
                    <img
                      src={setup.qrPngDataUrl}
                      alt="2FA QR code"
                      className="rounded-md border bg-white p-2"
                      width={224}
                      height={224}
                    />
                  </div>
                  <div className="grid gap-1">
                    <Label className="text-xs">Or enter this secret manually</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        readOnly
                        value={setup.secret}
                        className="font-mono text-xs"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(setup.secret);
                          toast.success("Secret copied");
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
              {error && (
                <p className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2">
                  {error}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={() => setStep("confirm")} disabled={!setup}>
                Next
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "confirm" && (
          <>
            <DialogHeader>
              <DialogTitle>Enter the 6-digit code</DialogTitle>
              <DialogDescription>
                Open your authenticator app and type the current code for the
                Booqly entry.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3">
              <Input
                inputMode="numeric"
                autoFocus
                maxLength={6}
                placeholder="123456"
                className="font-mono tracking-[0.4em] text-center text-lg h-12"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              {error && (
                <p className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2">
                  {error}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep("scan")}>
                Back
              </Button>
              <Button onClick={onConfirm} disabled={code.trim().length !== 6 || confirm.isPending}>
                {confirm.isPending ? "Verifying…" : "Confirm"}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "recovery" && (
          <>
            <DialogHeader>
              <DialogTitle>Save your recovery codes</DialogTitle>
              <DialogDescription>
                You'll only see these once. Each code lets you sign in if you
                lose access to your authenticator. Store them somewhere safe.
              </DialogDescription>
            </DialogHeader>
            <RecoveryCodesGrid codes={recoveryCodes} />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={savedAcked}
                onChange={(e) => setSavedAcked(e.target.checked)}
                className="h-4 w-4 rounded border-input"
              />
              I've saved my recovery codes somewhere safe.
            </label>
            <DialogFooter>
              <Button onClick={handleClose} disabled={!savedAcked}>
                <Check className="h-4 w-4 mr-1.5" /> Done
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function RecoveryCodesGrid({ codes }: { codes: string[] }) {
  const toast = useToast();
  const text = codes.join("\n");
  return (
    <div className="grid gap-2">
      <div className="grid grid-cols-2 gap-2 rounded-md border bg-muted/40 p-3 font-mono text-sm">
        {codes.map((c) => (
          <span key={c}>{c}</span>
        ))}
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => {
            navigator.clipboard.writeText(text);
            toast.success("Codes copied");
          }}
        >
          <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => {
            const blob = new Blob([text], { type: "text/plain" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "booqly-recovery-codes.txt";
            a.click();
            URL.revokeObjectURL(url);
          }}
        >
          <Download className="h-3.5 w-3.5 mr-1.5" /> Download
        </Button>
      </div>
    </div>
  );
}

function humanError(code: string): string {
  switch (code) {
    case "invalid_code":
      return "That code is incorrect. Try the next one.";
    case "setup_not_started":
      return "Setup didn't complete. Close and try again.";
    default:
      return code || "Couldn't confirm 2FA.";
  }
}
